/**
 * Multi-Layout Replay Service
 * 
 * Handles replay mode functionality for multi-chart layouts.
 * Processes candles and ticks to update each panel according to its timeframe.
 */

import { CandleData, TickData, Timeframe } from '../types';
import { ChartPanelConfig, LayoutType } from '../types/chartLayout';

// ============================================================================
// Types & Interfaces
// ============================================================================

/**
 * Timeframe duration in milliseconds
 */
export const TIMEFRAME_MS: Record<Timeframe, number> = {
  '1m': 60_000,
  '5m': 300_000,
  '15m': 900_000,
  '30m': 1_800_000,
  '1h': 3_600_000,
  '4h': 14_400_000,
  '1d': 86_400_000,
  '1w': 604_800_000,
};

/**
 * Tick data with index for replay processing
 */
export interface IndexedTick {
  tick: TickData;
  index: number;
}

/**
 * Result of processing replay data for a single panel
 */
export interface PanelReplayResult {
  panelId: string;
  candles: CandleData[];
  isNewCandle: boolean;
}

/**
 * Configuration for multi-layout replay update
 */
export interface MultiLayoutReplayConfig {
  layoutType: LayoutType;
  panels: ChartPanelConfig[];
  panelDataMap: Map<string, CandleData[]>;
}

/**
 * Options for updating panel data during replay
 */
export interface ReplayUpdateOptions {
  /** The next candle from the main replay (for candle mode) */
  nextCandle?: CandleData;
  /** The current replay time */
  replayTime: number;
  /** Ticks to process (for tick mode) */
  ticks?: IndexedTick[];
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Converts a timeframe string to milliseconds.
 * Returns 5m (300000ms) as default if timeframe is not recognized.
 */
export function getTimeframeMs(timeframe: string): number {
  return TIMEFRAME_MS[timeframe as Timeframe] ?? 300_000;
}

/**
 * Calculates the candle bucket start time for a given timestamp and timeframe.
 */
export function getCandleStartTime(timestamp: number, timeframeMs: number): number {
  return Math.floor(timestamp / timeframeMs) * timeframeMs;
}

/**
 * Creates a new candle from a tick or price data.
 */
export function createCandle(
  time: number,
  price: number,
  volume: number = 0
): CandleData {
  return {
    time,
    open: price,
    high: price,
    low: price,
    close: price,
    volume,
    dateStr: new Date(time).toLocaleTimeString(),
  };
}

/**
 * Updates an existing candle with new price/volume data.
 */
export function updateCandle(
  candle: CandleData,
  price: number,
  volume: number = 0
): CandleData {
  return {
    ...candle,
    close: price,
    high: Math.max(candle.high, price),
    low: Math.min(candle.low, price),
    volume: candle.volume + volume,
  };
}

/**
 * Updates a candle by aggregating another candle's OHLCV data.
 */
export function aggregateCandle(
  target: CandleData,
  source: CandleData
): CandleData {
  return {
    ...target,
    high: Math.max(target.high, source.high),
    low: Math.min(target.low, source.low),
    close: source.close,
    volume: target.volume + source.volume,
  };
}

// ============================================================================
// Core Replay Functions
// ============================================================================

/**
 * Processes a single tick for a panel's timeframe.
 * Returns updated candles array.
 */
export function processTickForPanel(
  panelCandles: CandleData[],
  tick: TickData,
  panelTimeframeMs: number
): CandleData[] {
  if (panelCandles.length === 0) {
    const startTime = getCandleStartTime(tick.time, panelTimeframeMs);
    return [createCandle(startTime, tick.price, tick.volume)];
  }

  const lastCandle = panelCandles[panelCandles.length - 1];
  const tickCandleStartTime = getCandleStartTime(tick.time, panelTimeframeMs);

  if (tickCandleStartTime > lastCandle.time) {
    // New candle period
    return [...panelCandles, createCandle(tickCandleStartTime, tick.price, tick.volume)];
  }

  // Update existing candle
  const updatedCandles = [...panelCandles];
  updatedCandles[updatedCandles.length - 1] = updateCandle(lastCandle, tick.price, tick.volume);
  return updatedCandles;
}

/**
 * Processes a source candle for a panel's timeframe (aggregation).
 * Returns updated candles array.
 * 
 * For panels with smaller timeframes than the source candle, this will
 * create multiple candles to fill the gap if needed.
 */
export function processCandleForPanel(
  panelCandles: CandleData[],
  sourceCandle: CandleData,
  panelTimeframeMs: number
): CandleData[] {
  if (panelCandles.length === 0) {
    const startTime = getCandleStartTime(sourceCandle.time, panelTimeframeMs);
    return [createCandle(startTime, sourceCandle.open, sourceCandle.volume)];
  }

  const lastCandle = panelCandles[panelCandles.length - 1];
  const candleStartTime = getCandleStartTime(sourceCandle.time, panelTimeframeMs);

  console.log('[processCandleForPanel]', {
    panelTimeframeMs,
    lastCandleTime: new Date(lastCandle.time).toLocaleString(),
    sourceCandleTime: new Date(sourceCandle.time).toLocaleString(),
    candleStartTime: new Date(candleStartTime).toLocaleString(),
    timeDiff: candleStartTime - lastCandle.time
  });

  if (candleStartTime > lastCandle.time) {
    // Check if we need to create multiple candles to fill the gap
    // This happens when source candle timeframe > panel timeframe
    const gapMs = candleStartTime - lastCandle.time;
    const candlesToCreate = Math.floor(gapMs / panelTimeframeMs);
    
    if (candlesToCreate > 1) {
      // Need to fill gap with intermediate candles
      // Use linear interpolation for price movement
      const priceStep = (sourceCandle.open - lastCandle.close) / candlesToCreate;
      const volumePerCandle = sourceCandle.volume / candlesToCreate;
      
      let updatedCandles = [...panelCandles];
      
      for (let i = 1; i <= candlesToCreate; i++) {
        const newCandleTime = lastCandle.time + (i * panelTimeframeMs);
        const isLastCandle = i === candlesToCreate;
        
        // For intermediate candles, interpolate price
        // For the last candle, use the actual source candle data
        const interpolatedPrice = lastCandle.close + (priceStep * i);
        
        const newCandle: CandleData = isLastCandle ? {
          time: newCandleTime,
          open: sourceCandle.open,
          high: sourceCandle.high,
          low: sourceCandle.low,
          close: sourceCandle.close,
          volume: volumePerCandle,
          dateStr: new Date(newCandleTime).toLocaleTimeString(),
        } : {
          time: newCandleTime,
          open: lastCandle.close + (priceStep * (i - 1)),
          high: Math.max(lastCandle.close + (priceStep * (i - 1)), interpolatedPrice),
          low: Math.min(lastCandle.close + (priceStep * (i - 1)), interpolatedPrice),
          close: interpolatedPrice,
          volume: volumePerCandle,
          dateStr: new Date(newCandleTime).toLocaleTimeString(),
        };
        
        updatedCandles = [...updatedCandles, newCandle];
      }
      
      console.log(`[processCandleForPanel] Created ${candlesToCreate} candles to fill gap`);
      return updatedCandles;
    }
    
    // Single new candle period for this panel
    const newCandle: CandleData = {
      time: candleStartTime,
      open: sourceCandle.open,
      high: sourceCandle.high,
      low: sourceCandle.low,
      close: sourceCandle.close,
      volume: sourceCandle.volume,
      dateStr: new Date(candleStartTime).toLocaleTimeString(),
    };
    return [...panelCandles, newCandle];
  }

  // Aggregate into existing candle
  const updatedCandles = [...panelCandles];
  updatedCandles[updatedCandles.length - 1] = aggregateCandle(lastCandle, sourceCandle);
  return updatedCandles;
}

/**
 * Updates all panels in a multi-layout with replay data.
 * 
 * @param config - Multi-layout configuration
 * @param options - Replay update options (candle or ticks)
 * @returns Updated panel data map
 */
export function updatePanelsForReplay(
  config: MultiLayoutReplayConfig,
  options: ReplayUpdateOptions
): Map<string, CandleData[]> {
  const { layoutType, panels, panelDataMap } = config;
  const { nextCandle, ticks } = options;

  // Single layout doesn't use panelDataMap
  if (layoutType === '1x1') {
    return panelDataMap;
  }

  const newMap = new Map(panelDataMap);

  for (const panel of panels) {
    const panelCandles = newMap.get(panel.id);
    if (!panelCandles || panelCandles.length === 0) {
      continue;
    }

    const panelTimeframeMs = getTimeframeMs(panel.timeframe);
    let updatedCandles = panelCandles;

    if (ticks && ticks.length > 0) {
      // Tick mode: process all ticks for this panel's timeframe
      for (const { tick } of ticks) {
        updatedCandles = processTickForPanel(updatedCandles, tick, panelTimeframeMs);
      }
    } else if (nextCandle) {
      // Candle mode: aggregate the source candle
      updatedCandles = processCandleForPanel(updatedCandles, nextCandle, panelTimeframeMs);
    }

    newMap.set(panel.id, updatedCandles);
  }

  return newMap;
}

/**
 * Truncates panel data to a specific replay start time.
 * Used when initializing replay mode.
 * 
 * @param panels - Panel configurations
 * @param panelDataMap - Current panel data
 * @param replayStartTime - Timestamp to truncate to
 * @returns Truncated panel data map
 */
export function truncatePanelDataForReplay(
  panels: ChartPanelConfig[],
  panelDataMap: Map<string, CandleData[]>,
  replayStartTime: number
): Map<string, CandleData[]> {
  const newMap = new Map(panelDataMap);

  for (const panel of panels) {
    const panelCandles = newMap.get(panel.id);
    if (!panelCandles || panelCandles.length === 0) {
      continue;
    }

    // Find the index where we should truncate for this panel
    // Use < replayStartTime to only show COMPLETED candles (avoid showing "future" data)
    const truncateIndex = panelCandles.findIndex(c => c.time >= replayStartTime);
    
    if (truncateIndex !== -1) {
      // Only include candles BEFORE the replay start time (completed candles only)
      const truncatedCandles = panelCandles.slice(0, truncateIndex);
      console.log(
        `[MultiLayoutReplay] Panel ${panel.id} (${panel.timeframe}): ` +
        `truncated from ${panelCandles.length} to ${truncatedCandles.length} candles`
      );
      newMap.set(panel.id, truncatedCandles);
    }
  }

  return newMap;
}

/**
 * Checks if the current layout is a multi-chart layout.
 */
export function isMultiLayout(layoutType: LayoutType): boolean {
  return layoutType !== '1x1';
}

/**
 * Gets the lowest (smallest) timeframe from all panels.
 * This is used to determine the replay step interval in multi-layout mode.
 * Returns the timeframe string and its duration in milliseconds.
 */
export function getLowestTimeframe(panels: ChartPanelConfig[]): { timeframe: Timeframe; ms: number } {
  if (panels.length === 0) {
    return { timeframe: '5m', ms: TIMEFRAME_MS['5m'] };
  }

  let lowestMs = Infinity;
  let lowestTimeframe: Timeframe = '5m';

  for (const panel of panels) {
    const tfMs = TIMEFRAME_MS[panel.timeframe as Timeframe] ?? TIMEFRAME_MS['5m'];
    if (tfMs < lowestMs) {
      lowestMs = tfMs;
      lowestTimeframe = panel.timeframe as Timeframe;
    }
  }

  return { timeframe: lowestTimeframe, ms: lowestMs };
}
